import { createEvent, type EventAttributes } from 'ics';
import { differenceInMinutes } from 'date-fns';
import { Button } from '@geberit/gdds';

// components
import { Translation } from 'components/Translation/Translation';

// utils
import { clickIcalLink } from './tracking-actions';
import { decodingContent } from 'utils/decodingContent';
import { useTracking } from 'utils/hooks/useTracking';

interface IcalendarProps {
  startDate: string;
  endDate: string;
  title: string;
  location?: string;
  description?: string;
  onlyIcon?: boolean;
}

function Icalendar({
  endDate,
  startDate,
  title,
  location,
  description,
  onlyIcon = false,
}: Readonly<IcalendarProps>) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const track = useTracking();

  const year = start.getFullYear();
  const month = start.getMonth();
  const day = start.getDate();
  const hours = start.getHours();
  const minutes = start.getMinutes();

  const currentEvent: EventAttributes = {
    start: [year, month + 1, day, hours, minutes],
    startInputType: 'local',
    startOutputType: 'local',
    duration: {
      days: 0,
      hours: 0,
      minutes: differenceInMinutes(end, start),
    },
    title,
    description: decodingContent(description),
    location,
  };

  const downloadEvent = () => {
    createEvent(currentEvent, (_, value) => {
      const blob = new Blob([value], { type: 'text/calendar' });
      const url = window.URL.createObjectURL(blob);

      track.trackEvent(clickIcalLink(url));

      const link = document.createElement('a');
      link.href = url;
      const fileName = 'event.ics';
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
    });
  };

  return (
    <Button
      onClick={downloadEvent}
      symbol="Calendar"
      stylingType="flat"
      iconAlignment="right"
      isIcon={onlyIcon}
    >
      {onlyIcon || <Translation id="group_investorcalendar_calendar" />}
    </Button>
  );
}

export default Icalendar;
